import {
  addToast,
  Button,
  Modal,
  SelectOptionType,
  TextAreaInput,
} from '@octano/global-ui';
import {
  forwardRef,
  Ref,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, Container, Row } from 'reactstrap';
import { useActionKitImp } from '../../../../components/ozmoit/common/action-kit-imp';
import { useValidations } from '../../../../hooks/useValidations';
import { TranslationsKeys } from '../../../../locales/translations';
import { createStudentRiskActionRecordAction } from './actions';

export type StudentRiskActionRecordModalMethods = {
  open: (actionId: string | number) => void;
  close: () => void;
};

type StudentRiskActionRecordModalProps = {
  onSave?: () => void;
};

type FormFields = {
  groupType: SelectOptionType | null;
  actionType: SelectOptionType | null;
  owner: string;
  notes: string;
};

const DEFAULT_VALUES: FormFields = {
  groupType: null,
  actionType: null,
  owner: '',
  notes: '',
};

const StudentRiskActionRecordModal = (
  { onSave }: StudentRiskActionRecordModalProps,
  ref: Ref<StudentRiskActionRecordModalMethods>,
) => {
  const { msgValidations } = useValidations();
  const { t } = useTranslation(
    TranslationsKeys.STUDENT_WARNINGS_STUDENT_HISTORY,
  );
  const [visible, setVisible] = useState(false);
  const [actionId, setActionId] = useState<string | number>();

  const { control, handleSubmit, reset, watch } = useForm<FormFields>({
    defaultValues: DEFAULT_VALUES,
  });

  const [notesValue] = watch(['notes']);

  const notesCount = useMemo(() => {
    return notesValue?.length ?? 0;
  }, [notesValue]);

  const { action: handleClickSave, loading } = useActionKitImp(
    createStudentRiskActionRecordAction,
    {
      onSuccess: () => {
        addToast({
          color: 'success',
          text: t(`studentActionsRecordsModal.success`),
        });
        !!onSave && onSave();
        setVisible(false);
      },
      onError: {
        unknown: () => {
          addToast({
            color: 'danger',
            text: t(`studentActionsRecordsModal.failed`),
          });
        },
      },
    },
  );

  const handleOpen = useCallback(
    (id: string | number) => {
      setActionId(id);
      setVisible(true);
      reset(DEFAULT_VALUES);
    },
    [reset],
  );

  const handleClose = useCallback(() => {
    setVisible(false);
    setActionId(undefined);
  }, []);

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  const handleSave = useCallback(
    async (data: FormFields) => {
      if (loading) {
        return;
      }
      handleClickSave({
        ...data,
        groupType: String(data.groupType?.value),
        actionType: String(data.actionType?.value),
        actionId: String(actionId),
      });
    },
    [loading, actionId, handleClickSave],
  );

  if (!visible) {
    return <></>;
  }

  return (
    <Modal isOpen={true} size="lg" toggle={handleClose}>
      <Container fluid>
        <Row>
          <Col xs={12}>
            <h2 className="text-dark fs-22 mb-0 w-100 text-center">
              {t(`studentActionsRecordsModal.title`)}
            </h2>
          </Col>
        </Row>
        <Row className="my-5">
          <Col xs={12} md={12}>
            <TextAreaInput
              control={control}
              name="notes"
              label={t(`studentActionsRecordsModal.notes`)}
              rules={{
                required: msgValidations.required,
              }}
              disabled={loading}
              maxLength={150}
            />
            <p>{`${notesCount} / 150`}</p>
          </Col>
        </Row>
        <Row>
          <Col xs={12} md={6}>
            <Button
              text={t('common:actions.cancel')}
              type="button"
              fullwidth
              outlined
              onClick={handleClose}
            />
          </Col>
          <Col xs={12} md={6}>
            <Button
              text={t('common:actions.save')}
              type="button"
              fullwidth
              onClick={handleSubmit(handleSave)}
              loading={loading}
            />
          </Col>
        </Row>
      </Container>
    </Modal>
  );
};

export default forwardRef(StudentRiskActionRecordModal);
